import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

export type ToastVariant = 'success' | 'error' | 'info';

type ToastState = {
  id: number;
  message: string;
  variant: ToastVariant;
} | null;

type ToastContextValue = {
  showToast: (message: string, variant?: ToastVariant) => void;
};

const ToastContext = createContext<ToastContextValue>({ showToast: () => {} });

function ToastView({ toast, onDismiss }: { toast: NonNullable<ToastState>; onDismiss: () => void }) {
  const styles: Record<ToastVariant, { bg: string; border: string; icon: string }> = {
    success: {
      bg: 'linear-gradient(135deg, rgba(6,148,148,0.95), rgba(5,120,120,0.95))',
      border: 'rgba(6,148,148,0.55)',
      icon: '✓',
    },
    error: {
      bg: 'linear-gradient(135deg, rgba(183,65,14,0.95), rgba(140,45,10,0.95))',
      border: 'rgba(255,140,90,0.45)',
      icon: '!',
    },
    info: {
      bg: 'linear-gradient(135deg, rgba(190,81,3,0.92), rgba(184,65,14,0.92))',
      border: 'rgba(255,206,27,0.4)',
      icon: '◆',
    },
  };
  const s = styles[toast.variant];
  return (
    <div
      role="status"
      className="pointer-events-auto flex max-w-[min(420px,92vw)] items-center gap-3 rounded-2xl border px-4 py-3 shadow-2xl"
      style={{
        background: s.bg,
        borderColor: s.border,
        color: '#fff',
        boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
        animation: 'odcToastIn 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
      }}
    >
      <span
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-black"
        style={{ background: 'rgba(0,0,0,0.2)' }}
      >
        {s.icon}
      </span>
      <p className="min-w-0 flex-1 text-sm font-semibold leading-snug">{toast.message}</p>
      <button
        type="button"
        className="shrink-0 rounded-lg px-2 py-1 text-xs font-bold opacity-90 hover:opacity-100"
        style={{ background: 'rgba(0,0,0,0.2)' }}
        onClick={onDismiss}
      >
        OK
      </button>
    </div>
  );
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toast, setToast] = useState<ToastState>(null);
  const dismissTimer = useRef<number | null>(null);

  const showToast = useCallback((message: string, variant: ToastVariant = 'info') => {
    const id = Date.now();
    if (dismissTimer.current != null) window.clearTimeout(dismissTimer.current);
    setToast({ id, message, variant });
    dismissTimer.current = window.setTimeout(() => {
      setToast((cur) => (cur?.id === id ? null : cur));
      dismissTimer.current = null;
    }, 4200);
  }, []);

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="pointer-events-none fixed bottom-6 left-1/2 z-[100000] flex -translate-x-1/2 flex-col items-center gap-2 px-3"
        aria-live="polite"
      >
        {toast ? <ToastView toast={toast} onDismiss={() => setToast(null)} /> : null}
      </div>
      <style>{`
        @keyframes odcToastIn {
          from { opacity: 0; transform: translateY(14px) scale(0.94); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
